// 🐢 PREMIOS NOA — el podio oficial del mes: GOP vs budget % del último mes
// PUBLICADO de Control de Gestión (la foto cg_hojas_publicadas, números
// congelados). Tocar un puesto abre la ficha completa del hotel (stats del
// premio + stats vivos). El trofeo es la TORTUGONA de oro/plata/bronce, con su
// equivalente en Caracolas 🐚 — y nunca se premia sobre el mes vivo.
import React, { useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { api, type HotelRow, type Premio, type PremioFila } from '@/api/client';
import { C, F, R, heatColor } from '@/constants/theme';
import { fmt, fmtDec, fmtE, fmtK, pct1 } from '@/lib/format';
import Tortugona, { type Metal } from './Tortugona';
import { BottomSheet, Card, Chip, KpiTile, PmsBadge, SectionTitle, Spark } from './ui';

const MESES = ['', 'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const METALES: Metal[] = ['oro', 'plata', 'bronce'];

/* 🔴 `soloSiHayPodio` — decisión de Patricio (25-08): «para dirección general los
   Premios Noa, hasta que no lo pongamos en marcha, se los podemos ocultar; dejalo
   visible en sistemas. A DG lo que le interesa es ver números, estadísticas,
   facturación, producción, ratios».
   No se oculta a lo bruto: con esta bandera la tarjeta desaparece MIENTRAS no
   haya podio, y reaparece sola el día que se publique la primera edición en
   Hotelos. En Sistemas se enseña siempre, porque ahí lo que interesa es
   justamente saber si está publicado o no. */
export default function PremioCard({ soloSiHayPodio = false }: { soloSiHayPodio?: boolean }) {
  const [premio, setPremio] = useState<Premio | null>(null);
  const [sel, setSel] = useState<{ fila: PremioFila; metal: Metal; hotel: HotelRow | null } | null>(null);

  useEffect(() => { api.premio().then((r) => setPremio(r.data)); }, []);

  const abrir = async (fila: PremioFila, i: number) => {
    setSel({ fila, metal: METALES[i], hotel: null });
    const hs = await api.hoteles();
    const hotel = hs.data.find((h) => (fila.id != null && h.id === fila.id) || h.n === fila.n) ?? null;
    setSel({ fila, metal: METALES[i], hotel });
  };

  if (!premio) return null;

  /* 🔴 PENDIENTE DE RESULTADOS. Los Premios Noa se fallan sobre el último mes
     PUBLICADO de Control de Gestión, y hasta que la publicación no está el
     podio no existe. Antes eso era una línea gris que parecía un error de la
     app; ahora es lo que de verdad es: un premio que todavía no se ha
     entregado. Las tres Tortugonas se ven en sombra — están, pero aún no
     tienen dueño. */
  if (!premio.disponible && soloSiHayPodio) return null;
  if (!premio.disponible) {
    return (
      <Card>
        <SectionTitle dot={C.gold} right="pendiente">Premios Noa</SectionTitle>
        <View style={s.pendPodio}>
          {METALES.map((metal, i) => (
            <View key={metal} style={[s.pendHueco, i === 0 && s.pendHuecoOro]}>
              <View style={{ opacity: 0.28 }}>
                <Tortugona metal={metal} size={i === 0 ? 34 : 27} />
              </View>
              <Text style={s.pendInterrog}>?</Text>
            </View>
          ))}
        </View>
        <Text style={s.pendTitulo}>Aún no hay ganadores</Text>
        <Text style={s.pendTxt}>
          {premio.motivo?.includes('publicad')
            ? 'El podio se falla sobre el cierre publicado en Control de Gestión. En cuanto se publique el mes, aquí aparecerán las tres Tortugonas con nombre y apellidos.'
            : (premio.motivo ?? 'En cuanto se publique el cierre del mes se conocerán los ganadores.')}
        </Text>
        <View style={s.pendPie}>
          <Text style={s.pendPieTxt}>🐚  Oro 3 · Plata 2 · Bronce 1 caracola</Text>
        </View>
      </Card>
    );
  }

  const titulo = premio.mes ? `Premios Noa · ${MESES[premio.mes]}${premio.anio ? ` ${premio.anio}` : ''}` : 'Premios Noa';

  /* ¿Hay algún mes ya terminado por detrás del premiado y todavía sin fallar?
     Solo cuentan los CERRADOS: el mes en curso no está en juego todavía. */
  const siguiente = (() => {
    if (!premio.anio || !premio.mes) return null;
    const hoy = new Date();
    const ultCerrado = new Date(hoy.getFullYear(), hoy.getMonth() - 1, 1);
    const premiado = new Date(premio.anio, premio.mes - 1, 1);
    if (premiado >= ultCerrado) return null;
    const sig = new Date(premio.anio, premio.mes, 1);
    return `${MESES[sig.getMonth() + 1]}${sig.getFullYear() !== premio.anio ? ` ${sig.getFullYear()}` : ''}`;
  })();
  const h = sel?.hotel;

  return (
    <>
      <Card>
        <SectionTitle dot={C.gold} right="publicado">{titulo}</SectionTitle>
        <View style={{ gap: 4 }}>
          {(premio.podio ?? []).map((p, i) => (
            <Pressable
              key={`${p.n}-${p.puesto}`}
              style={[s.row, i === 2 && { borderBottomWidth: 0 }, p.mio && s.rowMio]}
              onPress={() => abrir(p, i)}
            >
              <Tortugona metal={METALES[Math.min(i, 2)]} size={i === 0 ? 30 : 24} />
              <View style={{ flex: 1 }}>
                <Text style={[s.nombre, i === 0 && { fontSize: 15 }]} numberOfLines={1}>
                  {p.n.replace('Ona ', '')}{p.mio ? <Text style={{ color: C.aquaSoft }}>  · el tuyo</Text> : null}
                </Text>
                <Text style={s.gop}>
                  {p.detalle ?? (p.valor != null ? fmtDec(p.valor) : '')}
                  {p.caracolas ? `${p.detalle || p.valor != null ? ' · ' : ''}${p.caracolas} 🐚` : ''}
                </Text>
              </View>
              <Text style={[s.pct, i === 0 && { fontSize: 20, color: C.gold }]}>
                {['🥇', '🥈', '🥉'][i] ? `nº ${p.puesto}` : `nº ${p.puesto}`}
              </Text>
            </Pressable>
          ))}
        </View>
        {premio.mios?.length && !(premio.podio ?? []).some((p) => p.mio) ? (
          <Text style={s.tuyo}>
            {premio.mios.map((m) => `${m.n.replace('Ona ', '')}: nº ${m.puesto}`).join(' · ')}
          </Text>
        ) : null}
        <Text style={s.foot}>
          {premio.premio ?? 'Premios Noa'}
          {premio.publicadoPor ? ` · publicado por ${premio.publicadoPor}` : ''}
          {' — '}{premio.nota ?? 'Edición publicada en Hotelos › Premios Noa.'}
          {' Toca un puesto para su ficha.'}
        </Text>
        {/* Que haya pasado otro mes no significa que haya premio: lo falla una
            persona en Hotelos. Se dice que está en juego, no que llegará solo. */}
        {siguiente ? (
          <View style={s.proxima}>
            <Text style={s.proximaTxt}>
              {siguiente} está en juego — se sabrá cuando se publique su edición en Hotelos
            </Text>
          </View>
        ) : null}
      </Card>

      {/* Ficha del premiado. 🔴 Antes era un Modal propio con el tirador de
          ADORNO: se veía la línea y no arrastraba, había que tocar fuera para
          cerrar. Ahora usa la hoja compartida, que sí arrastra — y así el gesto
          es el mismo en toda la app en vez de depender de quién escribió cada
          pantalla. */}
      <BottomSheet visible={!!sel} onClose={() => setSel(null)}>
        {sel ? (
          <>
            <ScrollView showsVerticalScrollIndicator={false} bounces={false} overScrollMode="never">
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
                <Tortugona metal={sel.metal} size={44} />
                <View style={{ flex: 1 }}>
                  <Text style={s.sheetTitle}>{sel.fila.n}</Text>
                  <Text style={s.sub2}>{titulo}</Text>
                </View>
                <Text style={[s.pct, { fontSize: 22, color: C.gold }]}>nº {sel.fila.puesto}</Text>
              </View>

              {/* Lo que enseña la ficha es lo que PUBLICÓ la edición: su valor y
                  sus Caracolas. Ya no se recalcula un GOP aquí — si el premio
                  cambia de métrica, esta ficha sigue valiendo sin tocarla. */}
              <View style={{ flexDirection: 'row', gap: 10, marginTop: 14 }}>
                <KpiTile
                  value={sel.fila.detalle ?? (sel.fila.valor != null ? fmtDec(sel.fila.valor) : '—')}
                  label={premio.premio ?? 'Métrica del premio'} delta="edición publicada" deltaKind="flat" />
                <KpiTile
                  value={sel.fila.caracolas != null ? `${sel.fila.caracolas} 🐚` : '—'}
                  label="Caracolas" delta="al medallero" deltaKind="flat" />
              </View>

              {h ? (
                <>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 16, marginBottom: 8 }}>
                    <PmsBadge pms={h.pms} />
                    <Text style={s.sub2}>hoy, en vivo</Text>
                    <Spark vals={h.spark} width={86} height={20} />
                  </View>
                  <View style={{ gap: 10 }}>
                    <View style={{ flexDirection: 'row', gap: 10 }}>
                      <KpiTile value={`${fmt(Math.round(h.mes / 1e3))} k€`} label="Producción del mes" delta={h.d != null ? `ayer ${fmtE(h.d)}` : 'ayer sin volcar'} deltaKind="flat" />
                      <KpiTile value={h.occ != null ? pct1(h.occ) : '—'} label="Ocupación hoy" delta="rejilla 4 PMS" deltaKind="flat" />
                    </View>
                    <View style={{ flexDirection: 'row', gap: 10 }}>
                      <KpiTile value={h.adr != null ? `${fmtDec(h.adr)} €` : '—'} label="ADR del mes" delta={h.rn ? `${fmt(h.rn)} noches` : '—'} deltaKind="flat" />
                      <KpiTile value={h.ob ? `${h.ob} día(s)` : '0'} label="Overbooking 30 días" delta={h.ob ? '¡revisar!' : 'sin riesgo'} deltaKind={h.ob ? 'bad' : 'good'} />
                    </View>
                  </View>
                  {h.occ30?.length ? (
                    <View style={{ marginTop: 14 }}>
                      <Text style={s.heatLbl}>OCUPACIÓN · PRÓXIMOS 30 DÍAS</Text>
                      <View style={s.heatRow}>
                        {h.occ30.map((p, i) => (
                          <View key={i} style={[s.heatCell, { backgroundColor: p != null ? heatColor(p) : 'rgba(140,200,215,0.08)' }]} />
                        ))}
                      </View>
                    </View>
                  ) : null}
                </>
              ) : (
                <Text style={s.cargando}>Cargando stats vivos…</Text>
              )}
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 14 }}>
                <Chip>números publicados · congelados</Chip>
                <Chip>felicita al equipo 🐢</Chip>
              </View>
            </ScrollView>
          </>
        ) : null}
      </BottomSheet>
    </>
  );
}

const s = StyleSheet.create({
  rowMio: { backgroundColor: 'rgba(30,211,199,0.06)', borderRadius: 12 },
  tuyo: { fontFamily: F.uiSemi, fontSize: 11.5, color: C.aquaSoft, marginTop: 8 },
  proxima: { marginTop: 10, borderTopWidth: 1, borderTopColor: C.line, paddingTop: 10 },
  proximaTxt: { fontFamily: F.ui, fontSize: 11, color: C.aquaSoft, lineHeight: 16 },
  pendPodio: { flexDirection: 'row', justifyContent: 'center', alignItems: 'flex-end', gap: 14, marginTop: 6, marginBottom: 14 },
  pendHueco: {
    width: 62, height: 62, borderRadius: 20, alignItems: 'center', justifyContent: 'center',
    borderWidth: 1, borderStyle: 'dashed', borderColor: 'rgba(245,195,75,0.28)',
    backgroundColor: 'rgba(245,195,75,0.05)',
  },
  pendHuecoOro: { width: 72, height: 72, borderColor: 'rgba(245,195,75,0.45)' },
  pendInterrog: { position: 'absolute', bottom: 6, fontFamily: F.displayMed, fontSize: 13, color: 'rgba(245,195,75,0.55)' },
  pendTitulo: { fontFamily: F.displayMed, fontSize: 17, color: C.gold, textAlign: 'center' },
  pendTxt: { fontFamily: F.ui, fontSize: 12, color: C.muted, lineHeight: 18, textAlign: 'center', marginTop: 6, paddingHorizontal: 4 },
  pendPie: { alignSelf: 'center', marginTop: 12, borderWidth: 1, borderColor: 'rgba(245,195,75,0.25)', borderRadius: 999, paddingHorizontal: 13, paddingVertical: 6 },
  pendPieTxt: { fontFamily: F.uiSemi, fontSize: 11, color: C.gold, letterSpacing: 0.2 },
  row: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 9, borderBottomWidth: 1, borderBottomColor: C.line },
  nombre: { fontFamily: F.uiSemi, fontSize: 13.5, color: C.ink },
  gop: { fontFamily: F.ui, fontSize: 10.5, color: C.muted, marginTop: 2, fontVariant: ['tabular-nums'] },
  pct: { fontFamily: F.uiBold, fontSize: 16, color: C.good, fontVariant: ['tabular-nums'] },
  empty: { fontFamily: F.ui, fontSize: 12, color: C.muted, lineHeight: 17 },
  foot: { fontFamily: F.ui, fontSize: 10, color: C.faint, marginTop: 10, lineHeight: 14 },
  sheetTitle: { fontFamily: F.displayMed, fontSize: 19, color: C.ink },
  sub2: { fontFamily: F.ui, fontSize: 11.5, color: C.muted, marginTop: 2 },
  cargando: { fontFamily: F.ui, fontSize: 12, color: C.muted, marginTop: 16 },
  heatLbl: { fontFamily: F.uiSemi, fontSize: 10, letterSpacing: 1.3, color: C.muted, marginBottom: 6 },
  heatRow: { flexDirection: 'row', gap: 2 },
  heatCell: { flex: 1, height: 16, borderRadius: 3 },
});
